import {
    Teleport,
    defineComponent,
    ref,
} from "vue";

type CopyState = "idle" | "copied";
type TooltipPosition = {
    left: number;
    top: number;
};

async function copyText(value: string): Promise<void> {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return;
    }

    const input = document.createElement("textarea");
    input.value = value;
    input.setAttribute("readonly", "true");
    input.style.position = "fixed";
    input.style.opacity = "0";
    document.body.appendChild(input);
    input.select();
    document.execCommand("copy");
    input.remove();
}

function isCompactViewport(): boolean {
    return window.matchMedia("(max-width: 760px)").matches;
}

export default defineComponent({
    name: "CopyIdButton",

    props: {
        id: {
            type: String,
            required: true,
        },

        label: {
            type: String,
            default: "Identifiant",
        },
    },

    setup(props) {
        const state = ref<CopyState>("idle");
        const modalOpen = ref(false);
        const tooltipOpen = ref(false);
        const tooltipPosition = ref<TooltipPosition>({
            left: 0,
            top: 0,
        });
        let timeout: number | undefined;

        function updateTooltipPosition(element: HTMLElement) {
            const rect = element.getBoundingClientRect();
            tooltipPosition.value = {
                left: rect.left + rect.width / 2,
                top: rect.top - 6,
            };
        }

        function showTooltip(event: MouseEvent | FocusEvent) {
            if (isCompactViewport()) {
                return;
            }

            updateTooltipPosition(event.currentTarget as HTMLElement);
            tooltipOpen.value = true;
        }

        function hideTooltip() {
            if (state.value === "copied") {
                return;
            }

            tooltipOpen.value = false;
        }

        async function copy() {
            await copyText(props.id);
            state.value = "copied";
            tooltipOpen.value = !isCompactViewport();
            window.clearTimeout(timeout);
            timeout = window.setTimeout(() => {
                state.value = "idle";
                tooltipOpen.value = false;
            }, 1400);
        }

        async function handleClick(event: MouseEvent) {
            event.preventDefault();
            event.stopPropagation();

            if (isCompactViewport()) {
                modalOpen.value = true;
                return;
            }

            updateTooltipPosition(event.currentTarget as HTMLElement);
            await copy();
        }

        async function copyFromModal(event: MouseEvent) {
            event.preventDefault();
            event.stopPropagation();
            await copy();
            window.setTimeout(() => modalOpen.value = false, 450);
        }

        function closeModal(event?: MouseEvent) {
            event?.preventDefault();
            event?.stopPropagation();
            modalOpen.value = false;
        }

        return () => (
            <>
                <button
                    class={{ "copy-id-button": true, "copy-id-button-copied": state.value === "copied" }}
                    aria-label={`${props.label} ${props.id}`}
                    onClick={handleClick}
                    onFocus={showTooltip}
                    onBlur={hideTooltip}
                    onMouseenter={showTooltip}
                    onMouseleave={hideTooltip}
                >
                    <span>ID</span>
                </button>

                <Teleport to="body">
                    {(tooltipOpen.value || state.value === "copied") && !modalOpen.value && (
                        <div
                            class="copy-id-tooltip"
                            style={{
                                left: `${tooltipPosition.value.left}px`,
                                top: `${tooltipPosition.value.top}px`,
                            }}
                        >
                            {state.value === "copied" ? "Identifiant copié !" : props.id}
                        </div>
                    )}

                    {modalOpen.value && (
                        <div class="copy-id-modal-backdrop" onClick={closeModal}>
                            <div class="copy-id-modal" onClick={(event) => event.stopPropagation()}>
                                <div class="copy-id-modal-header">
                                    <div>
                                        <span>{props.label}</span>
                                        <strong>{props.id}</strong>
                                    </div>
                                    <button aria-label="Fermer" onClick={closeModal}>
                                        <i class="material-symbols-outlined">close</i>
                                    </button>
                                </div>
                                <button class="copy-id-modal-copy" onClick={copyFromModal}>
                                    <i class="material-symbols-outlined">{state.value === "copied" ? "check" : "content_copy"}</i>
                                    {state.value === "copied" ? "Identifiant copié !" : "Copier l’identifiant"}
                                </button>
                            </div>
                        </div>
                    )}
                </Teleport>
            </>
        );
    },
});
